"use client";

import React, {
  createContext,
  useContext,
  useEffect,
  useState,
  useCallback,
} from "react";
import { BASE_URL } from "../config/apiconfig";

/* ---------------- CONTEXT ---------------- */
const ShippingMethodContext = createContext(null);

export const useShippingMethods = () => {
  const context = useContext(ShippingMethodContext);
  if (!context) {
    throw new Error("useShippingMethods must be used within ShippingMethodProvider");
  }
  return context;
};

/* ---------------- API ENDPOINT ---------------- */
const SHIPPING_ENDPOINT = `${BASE_URL}/shipping-methods`;

/* ---------------- NORMALIZER ---------------- */
const normalizeShippingMethod = (m) => ({
  id: String(m.id),
  title: m.title ?? "",
  description: m.description ?? "",
  amount: Number(m.amount ?? 0),
  status: m.status ?? "Inactive",
  createdAt: m.create_audit_time ?? null,
});

/* ---------------- PROVIDER ---------------- */
export const ShippingMethodProvider = ({ children }) => {
  const [shippingMethods, setShippingMethods] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  const fetchShippingMethods = useCallback(async () => {
    try {
      setLoading(true);
      setError(null);

      const res = await fetch(SHIPPING_ENDPOINT, {
        headers: {
          "Content-Type": "application/json",
        },
      });

      const json = await res.json();

      // Supports:
      // { status: 200, data: [...] }
      // OR direct array
      const data = Array.isArray(json?.data)
        ? json.data
        : Array.isArray(json)
        ? json
        : [];

      setShippingMethods(data.map(normalizeShippingMethod));
    } catch (err) {
      console.error("Fetch shipping methods failed", err);
      setError("Failed to load shipping methods");
      setShippingMethods([]);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchShippingMethods();
  }, [fetchShippingMethods]);

  return (
    <ShippingMethodContext.Provider
      value={{
        shippingMethods,
        loading,
        error,
        refetch: fetchShippingMethods,
      }}
    >
      {children}
    </ShippingMethodContext.Provider>
  );
};
